import React, { useState, useEffect, useCallback } from 'react';
import { FiCheckCircle, FiCircle, FiChevronDown, FiChevronUp, FiInfo } from 'react-icons/fi';
import { useLanguage } from '../Context/LanguageContext';
import './QuickStartGuide.css';

const QuickStartGuide = ({ onUpdateLocalGuide, workspaceData }) => {
  const { t } = useLanguage();

  // Initialize expanded state from localStorage
  const [expanded, setExpanded] = useState(() => {
    const savedState = localStorage.getItem('quickStartGuideExpanded');
    return savedState !== null ? JSON.parse(savedState) : true;
  });

  // Create local state to track guide status
  const [localGuide, setLocalGuide] = useState({});

  // Save expanded state to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('quickStartGuideExpanded', JSON.stringify(expanded));
  }, [expanded]);

  // Initialize local guide from workspace data
  useEffect(() => {
    if (workspaceData?.quick_start_guide) {
      setLocalGuide(workspaceData.quick_start_guide);
    }
  }, [workspaceData]);

  // Function to refresh guide data (now just updates from workspace data)
  const refreshGuide = useCallback(() => {
    if (workspaceData?.quick_start_guide) {
      setLocalGuide(workspaceData.quick_start_guide);
    }
  }, [workspaceData]);

  // Function to update only the UI state without sending API request
  const updateLocalGuideOnly = useCallback((stepId, value) => {
    setLocalGuide(prev => ({
      ...prev,
      [stepId]: value
    }));
  }, []);

  // Expose the updateLocalGuideOnly function and refreshGuide function to parent components
  useEffect(() => {
    if (onUpdateLocalGuide) {
      onUpdateLocalGuide({
        updateLocalGuide: updateLocalGuideOnly,
        refreshGuide: refreshGuide
      });
    }
  }, [onUpdateLocalGuide, updateLocalGuideOnly, refreshGuide]);

  // Safety check if localGuide is empty or workspaceData is not available
  if (!workspaceData || Object.keys(localGuide).length === 0) return null;

  const steps = [
    {
      id: 'train',
      title: t('components.quickstartguide.trainTitle'),
      description: t('components.quickstartguide.trainDescription'),
      action: null, // Can't direct to specific agent training without ID
    },
    {
      id: 'create_support_bv',
      title: t('components.quickstartguide.createSupportBvTitle'),
      description: t('components.quickstartguide.createSupportBvDescription'),
      action: null,
    },
    {
      id: 'test_support_bv',
      title: t('components.quickstartguide.testSupportBvTitle'),
      description: t('components.quickstartguide.testSupportBvDescription'),
      action: null, // Can't direct to specific agent chat without ID
    },
    {
      id: 'customize_support_bv',
      title: t('components.quickstartguide.customizeSupportBvTitle'),
      description: t('components.quickstartguide.customizeSupportBvDescription'),
      action: null, // Can't direct to specific agent customize without ID
    },
    {
      id: 'integrate_support_bv',
      title: t('components.quickstartguide.integrateSupportBvTitle'),
      description: t('components.quickstartguide.integrateSupportBvDescription'),
      action: null, // Can't direct to specific agent integration without ID
    },
    {
      id: 'test_social_media_bv',
      title: t('components.quickstartguide.testSocialMediaBvTitle'),
      description: t('components.quickstartguide.testSocialMediaBvDescription'),
      action: null, // Can't direct to specific agent social media without ID
    },
    {
      id: 'test_image_gen_bv',
      title: t('components.quickstartguide.testImageGenBvTitle'),
      description: t('components.quickstartguide.testImageGenBvDescription'),
      action: null, // Can't direct to specific agent image generation without ID
    },
  ];

  // Calculate completion percentage based on the local state for immediate feedback
  const completedSteps = Object.values(localGuide).filter(Boolean).length;
  const totalSteps = Object.keys(localGuide).length;
  const completionPercentage = totalSteps > 0 ? (completedSteps / totalSteps) * 100 : 0;

  return (
    <div className="quick-start-guide">
      <div className="guide-header">
        <div className="d-flex align-items-center">
          <h5 className="mb-0 guide-title">
            {t('components.quickstartguide.title')}
          </h5>
          <div className="completion-badge ms-2">
            {Math.round(completionPercentage)}%
          </div>
        </div>
        <button
          className="expand-button"
          onClick={() => setExpanded(!expanded)}
          aria-label={expanded ? t('components.quickstartguide.collapseGuide') : t('components.quickstartguide.expandGuide')}
        >
          {expanded ? <FiChevronUp /> : <FiChevronDown />}
        </button>
      </div>

      {expanded && (
        <div className="guide-body">
          <div className="guide-steps">
            {steps.map((step) => (
              <div
                key={step.id}
                className={`guide-step ${localGuide[step.id] ? 'completed' : ''}`}
              >
                <div className="step-content-wrapper">
                  <div className="step-checkbox">
                    {localGuide[step.id] ?
                      <FiCheckCircle className="check-icon" /> :
                      <FiCircle className="uncheck-icon" />
                    }
                  </div>
                  <div className="step-content">
                    <h6 className="step-title">{step.title}</h6>
                    <p className="step-description">{step.description}</p>
                    {step.action && !localGuide[step.id] && (
                      <button
                        className="step-action-btn"
                        onClick={step.action}
                      >
                        {t('components.quickstartguide.goToAction')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="guide-footer">
            <div className="d-flex align-items-start">
              <FiInfo className="info-icon me-2" size={14} />
              <small className="footer-text">
                {t('components.quickstartguide.footerInfo')}
              </small>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuickStartGuide; 